import * as Sentry from '@sentry/browser';
import {consoleLogger, LogType} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {BaseErrorReporterWrapper, Tags, ExtraData} from './BaseErrorReporterWrapper';

const LogLevelMap: Record<number, Sentry.SeverityLevel> = {
    [LogType.Log]: 'log',
    [LogType.LogData]: 'log',
    [LogType.Error]: 'error',
    [LogType.Warning]: 'warning'
};

const BASE_64_PATTERN = ';base64,';

export class SentryErrorReporter extends BaseErrorReporterWrapper {
    // private initialScope: Tags = {};
    private enabled = true;

    public init(): void {
        const {gameName, gameVersion, framework, buildType, math, integration} = window.versions;

        this.setTags({
            'fw:gameName': gameName,
            'fw:framework': framework,
            'fw:gameVersion': gameVersion,
            'fw:buildType': buildType,
            'fw:math': math,
            'fw:integration': integration
        });

        Sentry.init({
            enabled: this.enabled,
            // debug: window.RP_CONFIG.buildProperty.env === 'local',
            environment: window.RP_CONFIG.buildProperty.env,
            dsn: window.RP_CONFIG.sentry_dsn,
            // Alternatively, use `process.env.npm_package_version` for a dynamic release version
            // if your build tool supports it.
            release: `${gameName}@${gameVersion}`,
            integrations: [
                Sentry.browserTracingIntegration()
                // TODO: investigate integrations
                // Sentry.replayCanvasIntegration(),
                // Sentry.sessionTimingIntegration(),
                // Sentry.browserProfilingIntegration()
                // Sentry.thirdPartyErrorFilterIntegration()
                // Sentry.replayIntegration()
            ],

            // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
            tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

            maxBreadcrumbs: 75,
            autoSessionTracking: false,
            profilesSampleRate: 0,
            tracesSampleRate: 1.0,

            // <MAYBE USEFUL OPTIONS>
            // initialScope: {},
            // autoSessionTracking: true,
            // sendClientReports: false,
            // </MAYBE USEFUL OPTIONS>

            // <ERROR FILTERING>
            // ignoreErrors: [/some pattern/gm],
            // denyUrls: [/some pattern/gm],
            // allowUrls: [/some pattern/gm],
            // </ERROR FILTERING>
            beforeBreadcrumb(logRecord) {
                const {category} = logRecord;
                const url = logRecord.data?.url;

                if (category === 'xhr' && typeof url === 'string' && url.includes(BASE_64_PATTERN)) {
                    return null;
                }

                return logRecord;
            },

            beforeSendSpan(span) {
                const url = span.data?.['http.url'];

                if (typeof url === 'string' && url.includes(BASE_64_PATTERN)) {
                    return null;
                }

                return span;
            },

            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0
        });

    }

    public setTags(tags: Tags): void {
        Object
            .entries(tags)
            .forEach(([key, val]) => Sentry.setTag(key, val));
    }

    public addExtraData(data: ExtraData): void {
        Sentry.setExtras(data);
    }

    public disable(): void {
        if (Sentry.isInitialized() && this.enabled) {
            Sentry.close();
        }

        this.enabled = false;
    }

    public receiver(logType: LogType, logData: any[]): void {
        if (logType === LogType.Write) {
            return;
        }

        const level = LogLevelMap[logType];
        let data;
        let message;

        if (logType === LogType.LogData) {
            message = logData[0];
            data = logData[1];
        } else {
            message = consoleLogger.stringifyObjects(logData);
        }

        Sentry.addBreadcrumb({level, data, message});
    }
}
